/**
 * What this platform is being used for.
 *
 * Vendor routing is one use of Kaoto, not the whole of it: the same canvas, toolbar and
 * DataMapper serve data syncing, standalone transformations and reusable assets just as well.
 * So the top of the workspace is a board of uses, and the vendors live inside the one that
 * needs them rather than being the root of everything.
 *
 * These are capabilities the platform ships, not rows an operator creates — which is why they
 * live here and not in the configuration. Adding a use is one entry plus the board that opens
 * inside it.
 */
export interface WorkspaceUse {
  id: string;
  name: string;
  /** One line on the box, saying what belongs in it. */
  summary: string;
  /** What opens inside: a board of vendors, a shared library, or nothing yet. */
  opens: 'vendors' | 'transforms' | 'none';
}

export const WORKSPACE_USES: WorkspaceUse[] = [
  {
    id: 'live-sync',
    name: 'Live sync',
    summary: 'Documents arriving from a vendor, mapped to the canonical model per tenant and business.',
    opens: 'vendors',
  },
  {
    id: 'transformations',
    name: 'Transformations',
    summary: 'A mapping that belongs to no vendor: authored and tested here, run by another system.',
    opens: 'transforms',
  },
];

/** The use that holds the vendors, which is what a vendor-or-deeper level sits inside. */
export const vendorUse = (): WorkspaceUse | undefined => WORKSPACE_USES.find((use) => use.opens === 'vendors');
